import Q from 'q';
import _ from 'underscore';

import type { ActionContext } from 'js/lib/ActionContext';

import type { SubmissionSchemaLinked } from 'bundles/assess-common/types/NaptimeSubmissionSchema';
import initializeSubmissionFromSchema from 'bundles/assess-common/utils/initializeSubmissionFromSchema';
import { loadCourseMaterials } from 'bundles/in-course-stores/actions/CourseMaterialsActions';
import { refreshCourseViewGrade } from 'bundles/in-course-stores/actions/CourseViewGradeActions';
import { getProgress } from 'bundles/in-course-stores/actions/ProgressActions';
import { loadSubmission } from 'bundles/peer/actions/SubmissionViewActions';
import { ActionNames } from 'bundles/peer/actions/constants';
import { getReviewQueue } from 'bundles/peer/api/onDemandPeerQueuedSubmissionSummariesApi';
import { getSubmissionDraft } from 'bundles/peer/api/onDemandPeerSubmissionDraftsApi';
import type { PeerAssignmentApiResponse } from 'bundles/peer/api/peerAssignmentApi';
import { getAssignment } from 'bundles/peer/api/peerAssignmentApi';
import {
  getPeerSubmissionProgresses,
  getPermissionsAndProgresses,
} from 'bundles/peer/api/peerPermissionsAndProgressesApi';
import { store as PeerAssignmentStoreClass } from 'bundles/peer/reducers/PeerAssignmentReducer';
import type { ReviewSchemaLinked } from 'bundles/peer/types/NaptimeReviewSchema';
import type { PeerPermissionsAndProgresses } from 'bundles/peer/types/PeerPermissionsAndProgresses';
import type { Capabilities } from 'bundles/peer/types/Permissions';
import type { OwnSubmissionAndRelated } from 'bundles/peer/types/Submission';
import {
  convertNaptimeAssignment,
  convertNaptimeReviewQueue,
  convertNaptimeSubmissionDraftToOwnSubmission,
  initializeOwnSubmissionFromNaptimeSchema,
} from 'bundles/peer/utils/responseConversionUtils';

const loadAssignment = (
  actionContext: ActionContext,
  { userId, courseId, itemId }: { userId: number; courseId: string; itemId: string }
): Q.Promise<PeerAssignmentApiResponse> => {
  actionContext.dispatch(ActionNames.LOADING_PEER_ASSIGNMENT);

  return getAssignment({
    userId,
    courseId,
    itemId,
  }).then((response) => {
    actionContext.dispatch(ActionNames.LOADED_PEER_ASSIGNMENT, {
      assignment: convertNaptimeAssignment(response),
      loadedItemId: itemId,
    });

    return response;
  });
};

export const loadPermissionsAndProgresses = (
  actionContext: ActionContext,
  { userId, courseId, itemId }: { userId: number; courseId: string; itemId: string }
): Q.Promise<PeerPermissionsAndProgresses> => {
  actionContext.dispatch(ActionNames.LOADING_PEER_USER_PERMISSIONS);

  return getPermissionsAndProgresses(userId, courseId, itemId).then((response) => {
    actionContext.dispatch(ActionNames.LOADED_DISPLAYABLE_PHASE_SCHEDULE, {
      displayablePhaseSchedule: response.displayablePhaseSchedules,
    });

    // @ts-expect-error TSMIGRATION
    const capabilities: Array<Capabilities> = _(response.assignmentPermissions)
      .chain()
      // only take capabilities that are allowed.
      .pick((value) => value.isAllowed)
      // take only the key (name of the permission)
      .keys()
      .value();

    actionContext.dispatch(ActionNames.LOADED_PEER_USER_PERMISSIONS, {
      capabilities,
      capabilitiesWithReasons: response.assignmentPermissions,
      receivedReviewProgresses: response.receivedReviewProgresses,
      submissionProgresses: response.submissionProgresses,
    });

    return response;
  });
};

export const loadLatestSubmissionProgresses = async (
  actionContext: ActionContext,
  { userId, courseId, itemId }: { userId: number; courseId: string; itemId: string }
) => {
  const submissionProgresses = await getPeerSubmissionProgresses({ userId, courseId, itemId });

  actionContext.dispatch(ActionNames.LOADED_PEER_USER_SUBMISSION_PROGRESSES, {
    submissionProgresses,
  });

  return submissionProgresses;
};

const loadSubmissionDraft = (
  actionContext: ActionContext,
  {
    userId,
    courseId,
    itemId,
    permissionsAndProgresses,
    submissionSchema,
    reviewSchema,
  }: {
    userId: number;
    courseId: string;
    itemId: string;
    permissionsAndProgresses: PeerPermissionsAndProgresses;
    submissionSchema: SubmissionSchemaLinked;
    reviewSchema?: ReviewSchemaLinked;
  }
) => {
  actionContext.dispatch(ActionNames.PEER_SUBMISSION_DRAFT_LOADING);

  if (permissionsAndProgresses.submissionProgresses.latestDraftSummary == null) {
    // if no draft, create draft submission from review and submission schema and load it into the draft store
    // @ts-expect-error TSMIGRATION
    return Q(initializeOwnSubmissionFromNaptimeSchema(submissionSchema, reviewSchema, userId)).then(
      (latestSubmissionAndRelated: OwnSubmissionAndRelated) =>
        actionContext.dispatch(ActionNames.LOADED_PEER_SUBMISSION_DRAFT, {
          naptimeSubmissionSchema: submissionSchema,
          naptimeSubmission: initializeSubmissionFromSchema(submissionSchema.submissionSchema, userId),
          latestSubmissionAndRelated,
        })
    );
  } else {
    // if draft exists, load draft
    return getSubmissionDraft({
      userId,
      courseId,
      itemId,
    }).then((naptimeDraft) => {
      const latestSubmissionAndRelated = convertNaptimeSubmissionDraftToOwnSubmission(naptimeDraft);
      return actionContext.dispatch(ActionNames.LOADED_PEER_SUBMISSION_DRAFT, {
        naptimeSubmission: naptimeDraft.submission,
        naptimeSubmissionSchema: submissionSchema,
        latestSubmissionAndRelated,
      });
    });
  }
};

const loadLatestSubmission = (
  actionContext: ActionContext,
  {
    userId,
    courseId,
    itemId,
    permissionsAndProgresses,
    submissionSchema,
    reviewSchema,
    shouldLoadOwnPeerViewSubmission,
  }: {
    userId: number;
    courseId: string;
    itemId: string;
    permissionsAndProgresses: PeerPermissionsAndProgresses;
    submissionSchema: SubmissionSchemaLinked;
    reviewSchema?: ReviewSchemaLinked;
    shouldLoadOwnPeerViewSubmission: boolean;
  }
) => {
  if (permissionsAndProgresses.submissionProgresses.latestSubmissionSummary == null) {
    return loadSubmissionDraft(actionContext, {
      userId,
      courseId,
      itemId,
      permissionsAndProgresses,
      submissionSchema,
      reviewSchema,
    });
  } else if (shouldLoadOwnPeerViewSubmission) {
    // if submission exists and load flag is true, load it into the submission store.
    return loadSubmission(actionContext, {
      userId,
      courseId,
      itemId,
      submissionId: permissionsAndProgresses.submissionProgresses.latestSubmissionSummary.computed.id,
    });
  } else {
    return Q();
  }
};

export const loadReviewQueue = (
  actionContext: ActionContext,
  {
    userId,
    itemId,
    courseId,
    limit,
    start,
  }: {
    userId: number;
    itemId: string;
    courseId: string;
    limit?: number;
    start?: number;
  }
) => {
  actionContext.dispatch(ActionNames.REVIEW_QUEUE_LOADING);

  return getReviewQueue({
    userId,
    courseId,
    itemId,
    start,
    limit,
  })
    .then((response) => {
      return convertNaptimeReviewQueue(response);
    })
    .then((data) => {
      actionContext.dispatch(ActionNames.REVIEW_QUEUE_LOADED, {
        data,
        itemId,
      });
    })
    .catch((error) => {
      if (error.status === 403) {
        actionContext.dispatch(ActionNames.REVIEW_QUEUE_ERROR, {
          errorType: 'mustSubmitBeforeReviewing',
        });
      } else {
        // display this error at some point.
        throw error;
      }
    });
};

/**
 * Loads data needs to display the Peer Review pages.
 */
export const loadInitialPeerData = (
  actionContext: ActionContext,
  {
    userId,
    courseId,
    itemId,
    forceRefresh,
    shouldLoadOwnPeerViewSubmission,
  }: {
    userId: number;
    courseId: string;
    itemId: string;
    forceRefresh?: boolean;
    shouldLoadOwnPeerViewSubmission: boolean;
  }
) => {
  // This data can already be loaded in from SSR. Check to see whether it has been.
  const peerAssignmentStoreState = actionContext.getStore(PeerAssignmentStoreClass).getState();
  const isStoreAlreadyLoaded = peerAssignmentStoreState.loadState === 'loaded';
  const hasItemIdChanged = itemId !== peerAssignmentStoreState.loadedItemId;
  if (!forceRefresh && isStoreAlreadyLoaded && !hasItemIdChanged) {
    return Q({});
  }

  return Q.all([
    loadPermissionsAndProgresses(actionContext, { userId, courseId, itemId }),
    loadAssignment(actionContext, { userId, courseId, itemId }),
  ]).spread((permissionsAndProgresses, peerAssignmentResponse) => {
    return loadLatestSubmission(actionContext, {
      userId,
      courseId,
      itemId,
      permissionsAndProgresses,
      submissionSchema: peerAssignmentResponse.linked['onDemandPeerSubmissionSchemas.v1'][0],
      reviewSchema: peerAssignmentResponse.linked['onDemandPeerReviewSchemas.v1'][0],
      shouldLoadOwnPeerViewSubmission,
    });
  });
};

export const refreshSubmissionAndProgress = (
  actionContext: ActionContext,
  {
    userId,
    courseId,
    courseSlug,
    itemId,
    forceRefresh = true,
  }: { userId: number; courseId: string; courseSlug: string; itemId: string; forceRefresh?: boolean }
) => {
  return loadInitialPeerData(actionContext, {
    userId,
    courseId,
    itemId,
    forceRefresh,
    shouldLoadOwnPeerViewSubmission: true,
    // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
  }).then(() => {
    // Refresh generic item progress APIs.
    return actionContext
      .executeAction(getProgress, {
        courseId,
        refreshProgress: true,
      })
      .then(() => {
        // Refresh generic item progress APIs.
        return Promise.all([
          actionContext.executeAction(getProgress, {
            courseId,
            refreshProgress: true,
          }),
          actionContext.executeAction(refreshCourseViewGrade, { courseId }),
          actionContext.executeAction(loadCourseMaterials, { courseSlug, refetch: true }),
        ]);
      });
  });
};
